import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LeadCaptureModal from '../components/LeadCaptureModal';

export default function Landing() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  const handleTestDrive = () => {
    setShowModal(true);
  };

  const handleLeadSubmit = (formData) => {
    setShowModal(false);
    navigate('/login?demo=true');
  };

  const handleBookDemo = () => {
    window.open('#', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Nav */}
      <nav className="flex justify-between items-center px-8 py-6">
        <div className="text-2xl font-bold text-white">
          <span className="text-amber-400">Virgo</span>
        </div>
        <button
          onClick={() => navigate('/login')}
          className="text-slate-300 hover:text-white transition"
        >
          Sign In
        </button>
      </nav>

      {/* Hero */}
      <main className="max-w-6xl mx-auto px-8 py-12">
        <div className="text-center">

          {/* LOGO */}
          <div className="flex justify-center mb-8">
            <img
              src="/logo.jpg"
              alt="Virgo Solo"
              className="h-40 md:h-52 w-auto drop-shadow-2xl rounded-2xl"
            />
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Practice management<br />
            <span className="text-amber-400">for the solo attorney.</span>
          </h1>

          <p className="text-xl text-slate-300 mb-12 max-w-2xl mx-auto">
            Cases. Clients. Calendar. Time tracking. Invoicing. Payments.
            Client portal. eSignature. AI assistant.
            <span className="text-white font-semibold"> All included. $99/month.</span>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleTestDrive}
              className="px-8 py-4 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-lg text-lg transition shadow-lg shadow-amber-500/25"
            >
              Take a Test Drive →
            </button>
            <button
              onClick={handleBookDemo}
              className="px-8 py-4 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg text-lg transition"
            >
              Book a Demo Call
            </button>
          </div>
        </div>

        {/* Features */}
        <div className="mt-24 grid md:grid-cols-3 gap-8">
          {[
            { icon: '⚖️', title: 'Cases & Contacts', desc: 'Manage your entire caseload. Track deadlines. Never miss a date.' },
            { icon: '⏱️', title: 'Time & Billing', desc: 'Track time. Generate invoices. Accept payments online via Square.' },
            { icon: '🔐', title: 'Client Portal', desc: 'Clients upload documents, sign forms, and message you securely.' },
            { icon: '✍️', title: 'eSignature', desc: 'Send documents for signature. No DocuSign fees.' },
            { icon: '📅', title: 'Calendar & Deadlines', desc: 'Court dates, meetings, and deadlines in one place.' },
            { icon: '🤖', title: 'AI Assistant', desc: 'Draft emails, summarize documents, prepare for hearings.' },
          ].map((feature, i) => (
            <div key={i} className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <div className="text-3xl mb-3">{feature.icon}</div>
              <h3 className="text-xl font-semibold text-amber-400 mb-2">{feature.title}</h3>
              <p className="text-slate-400">{feature.desc}</p>
            </div>
          ))}
        </div>

        {/* Pricing */}
        <div className="mt-24 text-center">
          <h2 className="text-3xl font-bold text-white mb-12">Simple Pricing</h2>

          <div className="flex flex-col md:flex-row gap-8 justify-center">
            <div className="bg-slate-800 rounded-2xl p-8 border-2 border-amber-500 max-w-sm">
              <h3 className="text-2xl font-bold text-white mb-2">Virgo Solo</h3>
              <div className="text-4xl font-bold text-white mb-4">
                $99<span className="text-lg text-slate-400">/month</span>
              </div>
              <p className="text-slate-400 mb-6">Everything a solo needs.</p>
              <ul className="text-left text-slate-300 space-y-2 mb-8">
                <li>✓ Unlimited cases & contacts</li>
                <li>✓ Time tracking & invoicing</li>
                <li>✓ Online payments (Square)</li>
                <li>✓ Client portal</li>
                <li>✓ eSignature</li>
                <li>✓ Calendar & deadlines</li>
                <li>✓ Document storage</li>
                <li>✓ AI assistant</li>
                <li>✓ Email integration</li>
              </ul>
              <button
                onClick={handleTestDrive}
                className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-lg transition"
              >
                Start Test Drive
              </button>
            </div>

            <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700 max-w-sm">
              <h3 className="text-2xl font-bold text-white mb-2">Virgo Pro</h3>
              <div className="text-4xl font-bold text-white mb-4">
                $199<span className="text-lg text-slate-400">/month</span>
              </div>
              <p className="text-slate-400 mb-6">For growing firms.</p>
              <ul className="text-left text-slate-300 space-y-2 mb-8">
                <li>✓ Everything in Solo</li>
                <li>✓ Multiple staff seats</li>
                <li>✓ Advanced AI features</li>
                <li>✓ Payroll management</li>
                <li>✓ Video conferencing</li>
                <li>✓ Intel & research tools</li>
                <li className="text-slate-500">Coming Q2 2026</li>
              </ul>
              <button
                onClick={handleBookDemo}
                className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg transition"
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-24 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Stop overpaying for Clio.
          </h2>
          <p className="text-xl text-slate-400 mb-8">
            Built by a solo, for solos. No per-user fees. No feature paywalls.
          </p>
          <button
            onClick={handleTestDrive}
            className="px-8 py-4 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-lg text-lg transition shadow-lg shadow-amber-500/25"
          >
            Take Virgo Solo for a Test Drive →
          </button>
        </div>

        {/* Footer */}
        <footer className="mt-24 pt-8 border-t border-slate-800 text-center text-slate-500">
          <p>© 2026 Astraea.AI, LLC. All rights reserved.</p>
          <p className="mt-2">
            <a href="mailto:hello@virgolaw.app" className="hover:text-slate-300">hello@virgolaw.app</a>
          </p>
        </footer>
      </main>

      {/* Lead Capture Modal */}
      <LeadCaptureModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleLeadSubmit}
      />
    </div>
  );
}
